// src/components/Inicio/Resumen.jsx
import React, { useEffect, useMemo, useState } from "react";
import usuarioService from "../../services/usuarioService";
import estanqueService from "../../services/estanqueService";
import dispositivoService from "../../services/dispositivoService";
import sensorService from "../../services/sensorService";
import efectorService from "../../services/efectorService";
import automationService from "../../services/automationService";
import { usePermisos } from "../../context/PermisosContext";
import { isSuperAdmin } from "../../services/authUtils";

// --- helpers ---
const parsePermisos = (p) => {
  if (!p) return [];
  if (Array.isArray(p)) return p.map(String);
  if (typeof p === "string")
    return p
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .map(String);
  return [];
};

const stringifySignalType = (v) => {
  if (typeof v === "string") return v;
  const map = { 0: "Analog", 1: "OneWire", 2: "Digital", 3: "Uart", 4: "I2c" };
  return map[v] ?? String(v);
};

const Resumen = ({ perfil }) => {
  const { can } = usePermisos();

  const [cargando, setCargando] = useState(true);
  const [err, setErr] = useState("");

  const [temporales, setTemporales] = useState([]);
  const [permanentes, setPermanentes] = useState([]); // queda N/D (no hay endpoint global)
  const [estanques, setEstanques] = useState([]);
  const [dispositivos, setDispositivos] = useState([]);
  const [sensoresPlanos, setSensoresPlanos] = useState([]);
  const [efectoresPlanos, setEfectoresPlanos] = useState([]);
  const [reglas, setReglas] = useState([]);

  const rolTxt = perfil?.rol || localStorage.getItem("rol") || "";

  // Superadmin: usa flag del JWT; si no está, cae a localStorage/rol por compat
  const esSuper = useMemo(() => {
    if (isSuperAdmin()) return true;
    if (localStorage.getItem("esSuperAdmin") === "true") return true;
    return (rolTxt || "").toLowerCase().includes("super");
  }, [rolTxt]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setCargando(true);
        setErr("");

        // ── Usuarios
        if (esSuper) {
          // Temporales (todos): existe
          try {
            const { data } = await usuarioService.listarPermisosTemporales();
            if (alive) setTemporales(Array.isArray(data) ? data : []);
          } catch {
            if (alive) setTemporales([]);
          }

          // Permanentes (global): SIN endpoint -> dejamos N/D
          if (alive) setPermanentes([]); // mostramos N/D en la tarjeta
        } else {
          // Temporales creados por este admin: existe
          try {
            const { data } = await usuarioService.obtenerUsuariosCreadosPorAdmin();
            if (alive) setTemporales(Array.isArray(data) ? data : []);
          } catch {
            if (alive) setTemporales([]);
          }
        }

        // ── Estanques (solo propios; no hay “listar global”)
        try {
          const data = await estanqueService.obtenerEstanques();
          if (alive) setEstanques(Array.isArray(data) ? data : []);
        } catch {
          if (alive) setEstanques([]);
        }

        // ── Dispositivos (solo propios)
        try {
          const data = await dispositivoService.obtenerMisDispositivos();
          if (alive) setDispositivos(Array.isArray(data) ? data : []);
        } catch {
          if (alive) setDispositivos([]);
        }

        // ── Sensores + catálogo
        try {
          const [sensoresRaw, tiposSensor] = await Promise.all([
            sensorService.obtenerTodosSensores(), // anidado
            sensorService.obtenerTiposSensor(), // [{id, nombre}]
          ]);

          const tipoMap = Object.fromEntries(
            (tiposSensor || []).map((t) => [Number(t.id), t.nombre])
          );

          const planos = (sensoresRaw || [])
            .flatMap((e) =>
              (e.dispositivos || []).map((d) => ({
                estanqueNombre: e.estanqueNombre,
                dispositivoId: d.dispositivoId,
                dispositivoNombre: d.dispositivoNombre,
                sensores: d.sensores || [],
              }))
            )
            .flatMap((x) =>
              x.sensores.map((s) => ({
                id: s.id,
                nombre: s.nombre ?? "",
                descripcion: s.descripcion ?? "",
                tipoParametroId:
                  Number(s.tipoParametroId ?? s.tipoId ?? 0) || null,
                tipoParametroNombre:
                  s.tipoParametroNombre ||
                  (s.tipoParametroId != null
                    ? tipoMap[Number(s.tipoParametroId)]
                    : "") ||
                  "",
                signalType: stringifySignalType(s.signalType),
                pinPrincipal: s.pinPrincipal ?? "",
                pinRx: s.pinRx ?? "",
                pinTx: s.pinTx ?? "",
                unidad: s.unidad ?? "",
                estaActivo: !!s.estaActivo,
                permisos: parsePermisos(s.permisos),
                estanqueNombre: x.estanqueNombre,
                dispositivoNombre: x.dispositivoNombre,
              }))
            );

          if (alive) setSensoresPlanos(planos);
        } catch {
          if (alive) setSensoresPlanos([]);
        }

        // ── Efectores + catálogo
        try {
          const [efectoresRaw, tiposEfector] = await Promise.all([
            efectorService.obtenerTodosLosEfectores(), // anidado
            efectorService.obtenerTiposEfector(), // [{id, nombre}]
          ]);

          const tipoMap = Object.fromEntries(
            (tiposEfector || []).map((t) => [Number(t.id), t.nombre])
          );

          const planos = (efectoresRaw || [])
            .flatMap((e) =>
              (e.dispositivos || []).map((d) => ({
                estanqueNombre: e.estanqueNombre,
                dispositivoId: d.dispositivoId,
                dispositivoNombre: d.dispositivoNombre,
                efectores: d.efectores || [],
              }))
            )
            .flatMap((x) =>
              x.efectores.map((f) => {
                const tipoId = Number(f.tipoEfectorId ?? f.tipoId);
                return {
                  id: f.id,
                  nombre: f.nombre,
                  descripcion: f.descripcion || "",
                  tipoEfectorId: tipoId || null,
                  tipoEfectorNombre:
                    f.tipoEfectorNombre || (tipoId ? tipoMap[tipoId] : "") || "",
                  pinActivacion: f.pinActivacion || "",
                  estaActivo: !!f.estaActivo,
                  encendido:
                    typeof f.encendido === "boolean" ? f.encendido : undefined,
                  permisos: parsePermisos(f.permisos),
                  estanqueNombre: x.estanqueNombre,
                  dispositivoNombre: x.dispositivoNombre,
                };
              })
            );

          if (alive) setEfectoresPlanos(planos);
        } catch {
          if (alive) setEfectoresPlanos([]);
        }

        // ── Reglas
        try {
          const data = await automationService.listarReglas();
          if (alive) setReglas(Array.isArray(data) ? data : []);
        } catch {
          if (alive) setReglas([]);
        }
      } catch {
        if (alive) setErr("No se pudo cargar el resumen.");
      } finally {
        if (alive) setCargando(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [esSuper]);

  // ─── Derivados ───────────────────────────────────────────────
  const sensoresPorTipo = useMemo(() => {
    const map = {};
    for (const s of sensoresPlanos) {
      const tipo = s?.tipoParametroNombre?.trim()
        ? s.tipoParametroNombre
        : "Sin tipo";
      map[tipo] = (map[tipo] || 0) + 1;
    }
    return map;
  }, [sensoresPlanos]);

  const efectoresPorTipo = useMemo(() => {
    const map = {};
    for (const e of efectoresPlanos) {
      const tipo = e?.tipoEfectorNombre?.trim()
        ? e.tipoEfectorNombre
        : "Sin tipo";
      map[tipo] = (map[tipo] || 0) + 1;
    }
    return map;
  }, [efectoresPlanos]);

  const permanentesPorRol = useMemo(() => {
    if (!esSuper) return {};
    const map = {};
    for (const u of permanentes) {
      const r = (u?.rol || "Sin rol").toString();
      map[r] = (map[r] || 0) + 1;
    }
    return map;
  }, [permanentes, esSuper]);

  const temporalesPorRol = useMemo(() => {
    const map = {};
    for (const u of temporales) {
      const r = (u?.rol || "Temporal").toString();
      map[r] = (map[r] || 0) + 1;
    }
    return map;
  }, [temporales]);

  // ─── UI helpers (número con color distinto) ──────────────────
  const Card = ({ title, value, children }) => (
    <div className="rounded-xl border border-emerald-800/40 bg-neutral-900/70 p-4 shadow-sm">
      <div className="flex items-baseline justify-between">
        <h3 className="font-medium text-neutral-300">{title}</h3>
        <span className="text-2xl font-extrabold text-cyan-400">{value}</span>
      </div>
      {children && <div className="mt-2 text-sm text-neutral-300">{children}</div>}
    </div>
  );

  if (cargando)
    return <div className="text-neutral-300 animate-pulse">Cargando resumen…</div>;

  if (err)
    return (
      <div className="bg-red-900/30 border border-red-600/60 text-red-100 px-4 py-3 rounded-lg">
        {err}
      </div>
    );

  return (
    <section className="space-y-6">
      {/* Fila 1: Usuarios */}
      <div className="grid md:grid-cols-3 gap-4">
        {esSuper ? (
          <>
            <Card title="Usuarios permanentes (total)" value={permanentes.length || "N/D"}>
              {Object.keys(permanentesPorRol).length ? (
                <ul className="mt-1 space-y-1">
                  {Object.entries(permanentesPorRol).map(([rol, n]) => (
                    <li key={rol} className="flex justify-between">
                      <span className="text-neutral-400">{rol}</span>
                      <span className="font-semibold text-cyan-300">{n}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-neutral-500">Sin desglose por rol.</p>
              )}
            </Card>

            <Card title="Usuarios temporales (total)" value={temporales.length}>
              {Object.keys(temporalesPorRol).length ? (
                <ul className="mt-1 space-y-1">
                  {Object.entries(temporalesPorRol).map(([rol, n]) => (
                    <li key={rol} className="flex justify-between">
                      <span className="text-neutral-400">{rol}</span>
                      <span className="font-semibold text-cyan-300">{n}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Card>

            <div className="rounded-xl border border-emerald-800/40 bg-neutral-900/70 p-4 shadow-sm">
              <h3 className="font-medium text-neutral-300">Tu rol</h3>
              <div className="mt-1 flex items-center gap-2">
                <p className="text-neutral-200 capitalize">{rolTxt || "—"}</p>
                <span className="inline-flex items-center rounded-full border border-cyan-600/60 px-2 py-[2px] text-xs text-cyan-300">
                  Superadministrador
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <Card title="Mis usuarios temporales" value={temporales.length}>
              <p className="text-neutral-500">Temporales creados por ti.</p>
            </Card>
            <div className="rounded-xl border border-emerald-800/40 bg-neutral-900/70 p-4 shadow-sm md:col-span-2">
              <h3 className="font-medium text-neutral-300">Tu rol</h3>
              <p className="text-neutral-200 mt-1 capitalize">{rolTxt || "—"}</p>
              <p className="text-neutral-500 text-sm mt-1">Resumen limitado a tus recursos.</p>
            </div>
          </>
        )}
      </div>

      {/* Fila 2: Inventario / Operación */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card title="Estanques" value={estanques.length || "N/D"} />
        <Card title="Dispositivos" value={dispositivos.length || "N/D"} />
        <Card title="Reglas de automatización" value={reglas.length || "N/D"} />
        <div className="rounded-xl border border-emerald-800/40 bg-neutral-900/70 p-4 shadow-sm">
          <h3 className="font-medium text-neutral-300">Permisos</h3>
          <p className="text-neutral-200 mt-1">
            {can("USUARIOS_TEMP_VIEW")
              ? "Puedes ver usuarios temporales."
              : "Sin acceso a usuarios temporales."}
          </p>
        </div>
      </div>

      {/* Fila 3: Clasificaciones */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card
          title="Sensores por tipo"
          value={Object.values(sensoresPorTipo).reduce((a, b) => a + b, 0) || "N/D"}
        >
          {Object.keys(sensoresPorTipo).length ? (
            <ul className="mt-1 space-y-1">
              {Object.entries(sensoresPorTipo).map(([tipo, n]) => (
                <li key={tipo} className="flex justify-between">
                  <span className="text-neutral-400">{tipo}</span>
                  <span className="font-semibold text-cyan-300">{n}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-neutral-500">Sin datos.</p>
          )}
        </Card>

        <Card
          title="Efectores por tipo"
          value={Object.values(efectoresPorTipo).reduce((a, b) => a + b, 0) || "N/D"}
        >
          {Object.keys(efectoresPorTipo).length ? (
            <ul className="mt-1 space-y-1">
              {Object.entries(efectoresPorTipo).map(([tipo, n]) => (
                <li key={tipo} className="flex justify-between">
                  <span className="text-neutral-400">{tipo}</span>
                  <span className="font-semibold text-cyan-300">{n}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-neutral-500">Sin datos.</p>
          )}
        </Card>
      </div>
    </section>
  );
};

export default Resumen;
